import React, { useEffect, useState, useRef, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { fetchAuthSession, signOut } from "aws-amplify/auth";

const IDLE_TIMEOUT = 15 * 60 * 1000;    // 15 minutes
const WARNING_BEFORE = 2 * 60 * 1000;   // Show warning at 13 minutes
const ACTIVITY_EVENTS = ["mousedown", "keydown", "mousemove", "touchstart", "scroll"];

function ProtectedRoute({ children }) {
    const [status, setStatus] = useState("loading");
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const navigate = useNavigate();

    const idleTimer = useRef(null);
    const warningTimer = useRef(null);
    const countdownInterval = useRef(null);

    const handleLogout = useCallback(async () => {
        clearTimeout(idleTimer.current);
        clearTimeout(warningTimer.current);
        clearInterval(countdownInterval.current);
        setShowWarning(false);
        try { await signOut(); } catch {}
        navigate("/sign-in", { replace: true });
    }, [navigate]);

    const resetTimers = useCallback(() => {
        clearTimeout(idleTimer.current);
        clearTimeout(warningTimer.current);
        clearInterval(countdownInterval.current);
        setShowWarning(false);

        warningTimer.current = setTimeout(() => {
            setShowWarning(true);
            const expiry = Date.now() + WARNING_BEFORE;
            countdownInterval.current = setInterval(() => {
                const remaining = Math.max(0, Math.ceil((expiry - Date.now()) / 1000));
                setCountdown(remaining);
                if (remaining <= 0) {
                    clearInterval(countdownInterval.current);
                }
            }, 1000);
        }, IDLE_TIMEOUT - WARNING_BEFORE);

        idleTimer.current = setTimeout(() => {
            handleLogout();
        }, IDLE_TIMEOUT);
    }, [handleLogout]);

    useEffect(() => {
        const verify = async () => {
            try {
                const session = await fetchAuthSession({ forceRefresh: false });
                if (!session.tokens?.idToken) {
                    setStatus("unauthenticated");
                    return;
                }
                setStatus("authenticated");
            } catch (err) {
                console.error('ProtectedRoute auth check failed:', err);
                setStatus("unauthenticated");
            }
        };
        verify();
    }, []);

    useEffect(() => {
        if (status !== "authenticated") return;

        resetTimers();

        const onActivity = () => {
            if (!showWarning) resetTimers();
        };

        ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity));

        return () => {
            ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
            clearTimeout(idleTimer.current);
            clearTimeout(warningTimer.current);
            clearInterval(countdownInterval.current);
        };
    }, [status, resetTimers, showWarning]);

    if (status === "loading") return null;
    if (status === "unauthenticated") return <Navigate to="/sign-in" replace />;

    return (
        <>
            {children}
            {showWarning && (
                <div className="idle-timeout-overlay">
                    <div className="idle-timeout-modal">
                        <h3>Session Expiring</h3>
                        <p>You will be logged out in <strong>{countdown}</strong> seconds due to inactivity.</p>
                        <div className="idle-timeout-actions">
                            <button className="idle-timeout-stay" onClick={resetTimers}>Stay Logged In</button>
                            <button className="idle-timeout-logout" onClick={handleLogout}>Log Out</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default ProtectedRoute;
